/**
 * SchemaCompareObjectPicker - Object selector for single-object comparison
 *
 * Displays a combined list of tables, views, procedures, functions and (SQL
 * Server only, S7) user-defined types in a searchable dropdown with type
 * filtering.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Table2, Eye, Cog, FunctionSquare, Search, ChevronsUpDown, Check, Shapes } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import {
  getTables,
  getViews,
  getStoredProcedures,
  getFunctions,
  getOracleObjects,
} from '../../../database-objects/services/objectsService';
import type { DatabaseObject } from '../../../database-objects/models/databaseObjectTypes';
import type { SchemaCompareObjectType } from '../types/schemaCompare';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
export type ObjectPickerValue = {
  objectType?: SchemaCompareObjectType;
  objectSchema?: string;
  objectName?: string;
};

type SchemaCompareObjectPickerProps = {
  connectionId: string | undefined;
  database: string | undefined;
  engineId: string;
  value: ObjectPickerValue;
  onChange: (value: ObjectPickerValue) => void;
  testIdPrefix: string;
};

type DatabaseObjectWithType = DatabaseObject & {
  objectType: SchemaCompareObjectType;
};

const OBJECT_TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'table', label: 'Tables' },
  { value: 'view', label: 'Views' },
  { value: 'procedure', label: 'Procs' },
  { value: 'function', label: 'Funcs' },
  // S7 — SQL Server user-defined types; the chip is hidden on other engines.
  { value: 'userType', label: 'Types' },
] as const;

/** The explorer's three user-type listings, folded into one picker kind. */
const MSSQL_USER_TYPE_KINDS = ['table-type', 'alias-type', 'clr-type'] as const;

type ObjectTypeFilter = typeof OBJECT_TYPE_FILTERS[number]['value'];

const getObjectIcon = (type: SchemaCompareObjectType) => {
  switch (type) {
    case 'table':
      return Table2;
    case 'view':
      return Eye;
    case 'procedure':
      return Cog;
    case 'function':
      return FunctionSquare;
    case 'userType':
      return Shapes;
    default:
      return Table2;
  }
};

export function SchemaCompareObjectPicker({
  connectionId,
  database,
  engineId,
  value,
  onChange,
  testIdPrefix,
}: SchemaCompareObjectPickerProps) {
  // Dropdown state
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // State for objects
  const [objects, setObjects] = useState<DatabaseObjectWithType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Filter state
  const [typeFilter, setTypeFilter] = useState<ObjectTypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Fetch all objects when connection/database changes
  useEffect(() => {
    if (!connectionId || !database) {
      setObjects([]);
      setError(undefined);
      return;
    }

    let cancelled = false;

    async function fetchAllObjects() {
      setIsLoading(true);
      setError(undefined);

      try {
        // Fetch all object types in parallel. User-defined types are SQL
        // Server only (S7) — the other engines have no listing for them.
        const [tablesResult, viewsResult, proceduresResult, functionsResult, ...userTypeResults] =
          await Promise.all([
            getTables(connectionId!, database!, engineId),
            getViews(connectionId!, database!, engineId),
            getStoredProcedures(connectionId!, database!, engineId),
            getFunctions(connectionId!, database!, engineId),
            ...(engineId === 'mssql'
              ? MSSQL_USER_TYPE_KINDS.map((kind) =>
                  getOracleObjects(connectionId!, database!, kind, engineId),
                )
              : []),
          ]);

        if (cancelled) return;

        const allObjects: DatabaseObjectWithType[] = [
          ...tablesResult.tables.map((t) => ({ ...t, objectType: 'table' as const })),
          ...viewsResult.views.map((v) => ({ ...v, objectType: 'view' as const })),
          ...proceduresResult.procedures.map((p) => ({ ...p, objectType: 'procedure' as const })),
          ...functionsResult.functions.map((f) => ({ ...f, objectType: 'function' as const })),
          ...userTypeResults.flatMap((r) =>
            r.objects.map((t) => ({ ...t, objectType: 'userType' as const })),
          ),
        ];

        // Sort by schema then name
        allObjects.sort((a, b) => {
          const schemaCompare = a.schema.localeCompare(b.schema);
          if (schemaCompare !== 0) return schemaCompare;
          return a.name.localeCompare(b.name);
        });

        setObjects(allObjects);

        // Check for errors
        const errors = [
          tablesResult.error,
          viewsResult.error,
          proceduresResult.error,
          functionsResult.error,
          ...userTypeResults.map((r) => r.error),
        ].filter(Boolean);

        if (errors.length > 0) {
          setError(errors.join('; '));
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load objects');
        setObjects([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchAllObjects();

    return () => {
      cancelled = true;
    };
  }, [connectionId, database, engineId]);

  // Filter objects based on type and search
  const filteredObjects = useMemo(() => {
    let result = objects;

    // Filter by type
    if (typeFilter !== 'all') {
      result = result.filter((obj) => obj.objectType === typeFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (obj) =>
          obj.name.toLowerCase().includes(query) ||
          obj.schema.toLowerCase().includes(query)
      );
    }

    return result;
  }, [objects, typeFilter, searchQuery]);

  // Handle object selection
  const handleObjectSelect = useCallback(
    (obj: DatabaseObjectWithType) => {
      onChange({
        objectType: obj.objectType,
        objectSchema: obj.schema,
        objectName: obj.name,
      });
      setOpen(false);
    },
    [onChange]
  );

  // Get selected object for display
  const selectedObject = useMemo(() => {
    if (!value.objectType || !value.objectName) {
      return undefined;
    }
    return objects.find(
      (o) =>
        o.objectType === value.objectType &&
        o.schema === value.objectSchema &&
        o.name === value.objectName
    );
  }, [objects, value]);

  // Count objects by type
  const objectCounts = useMemo(() => {
    return {
      all: objects.length,
      table: objects.filter((o) => o.objectType === 'table').length,
      view: objects.filter((o) => o.objectType === 'view').length,
      procedure: objects.filter((o) => o.objectType === 'procedure').length,
      function: objects.filter((o) => o.objectType === 'function').length,
      userType: objects.filter((o) => o.objectType === 'userType').length,
    };
  }, [objects]);

  const isSqlite = engineId === 'sqlite';
  // S7 — the Types chip only where user-defined types can be listed at all.
  const visibleTypeFilters = OBJECT_TYPE_FILTERS.filter(
    (filter) => filter.value !== 'userType' || engineId === 'mssql',
  );

  // Reset state when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setHighlightedIndex(0);
    }
  }, [open]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredObjects.length]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [open]);

  // Global Escape key handler
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [open]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const item = list?.children[highlightedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            Math.min(prev + 1, filteredObjects.length - 1)
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredObjects[highlightedIndex]) {
            handleObjectSelect(filteredObjects[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          setOpen(false);
          break;
        case 'Tab':
          setOpen(false);
          break;
      }
    },
    [filteredObjects, highlightedIndex, handleObjectSelect]
  );

  const handleTriggerClick = useCallback(() => {
    if (connectionId && database && !isLoading) {
      setOpen((prev) => !prev);
    }
  }, [connectionId, database, isLoading]);

  return (
    <div ref={containerRef} className="relative w-full" data-testid={`${testIdPrefix}-object-picker`}>
      {/* Trigger button */}
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={!connectionId || !database || isLoading}
        onClick={handleTriggerClick}
        className={cn(
          'w-full justify-between font-normal',
          !selectedObject && 'text-muted-foreground'
        )}
        data-testid={`${testIdPrefix}-object-select`}
      >
        {isLoading ? (
          <span className="flex items-center gap-2 text-muted-foreground">
            <RandomDotmSpinner size={12} />
            Loading objects...
          </span>
        ) : selectedObject ? (
          <span className="flex items-center gap-2">
            {(() => {
              const Icon = getObjectIcon(selectedObject.objectType);
              return <Icon className="h-3.5 w-3.5" />;
            })()}
            {!isSqlite && selectedObject.schema && (
              <span className="text-muted-foreground">{selectedObject.schema}.</span>
            )}
            <span>{selectedObject.name}</span>
          </span>
        ) : (
          <span>Select object...</span>
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            'absolute left-0 top-full z-50 mt-1 w-full',
            'rounded-md border bg-popover text-popover-foreground shadow-md',
            'animate-in fade-in-0 zoom-in-95'
          )}
        >
          <div className="flex flex-col">
            {/* Search input */}
            <div className="flex items-center border-b px-3 py-2">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search objects..."
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-testid={`${testIdPrefix}-object-search`}
              />
            </div>

            {/* Type filter chips - inside dropdown */}
            <div className="flex flex-wrap gap-1 px-2 py-1.5 border-b">
              {visibleTypeFilters.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  className={cn(
                    'h-5 text-[10px] px-1.5 rounded-sm transition-colors',
                    typeFilter === filter.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                  )}
                  onClick={() => setTypeFilter(filter.value)}
                  data-testid={`${testIdPrefix}-filter-${filter.value}`}
                >
                  {filter.label}
                  <span className="ml-0.5 opacity-70">
                    ({objectCounts[filter.value]})
                  </span>
                </button>
              ))}
            </div>

            {/* Options list */}
            <div
              ref={listRef}
              className="max-h-[200px] overflow-y-auto p-1"
              role="listbox"
            >
              {filteredObjects.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {error || (searchQuery ? 'No matching objects' : 'No objects found')}
                </div>
              ) : (
                filteredObjects.map((obj, index) => {
                  const Icon = getObjectIcon(obj.objectType);
                  const isSelected = selectedObject?.id === obj.id;
                  return (
                    <div
                      key={obj.id}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={-1}
                      className={cn(
                        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                        'hover:bg-accent hover:text-accent-foreground',
                        isSelected && 'bg-accent/50',
                        highlightedIndex === index && 'bg-accent text-accent-foreground'
                      )}
                      onClick={() => handleObjectSelect(obj)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <Icon className="mr-2 h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      {!isSqlite && obj.schema && (
                        <span className="text-muted-foreground">{obj.schema}.</span>
                      )}
                      <span className="truncate">{obj.name}</span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Count indicator */}
            {objects.length > 0 && (
              <div className="border-t px-3 py-1.5 text-xs text-muted-foreground">
                {searchQuery || typeFilter !== 'all'
                  ? `${filteredObjects.length} of ${objects.length}`
                  : `${objects.length} ${objects.length === 1 ? 'object' : 'objects'}`}
              </div>
            )}
          </div>
        </div>
      )}

      {error && objects.length > 0 && (
        <p className="text-xs text-warning mt-1">Warning: {error}</p>
      )}
    </div>
  );
}
